import React from 'react';
import Highlighter from 'react-highlight-words';
import { CaretDownFilled, CaretUpFilled } from '@ant-design/icons';

import { Button, renderResetCredentialsButton } from '../../../components';
import { renderRecordValue } from '../../../utils';
import { renderOrgStatus } from '../../../utils/helpers';
import { USER_ROLES, USER_STATUSES } from '../../../constants/constants';

import styles from './index.module.css';

export const renderColumns = (searchValue, highlighted, { expandedRowKeys = [], onToggleExpand } = {}) => [
  {
    title: 'Names',
    dataIndex: 'name',
    key: 'name',
    width: 340,
    render: (text, record) => {
      const isExpanded = expandedRowKeys.includes(record.key);
      const hasChildren = Array.isArray(record.children) && record.children.length > 0;

      return (
      <div className={styles.nameCell}>
        <span className={styles.cellValue}>
          {searchValue && highlighted ? (
            <Highlighter
              highlightStyle={{ backgroundColor: '# var(--ant-higlighter-color)', padding: 0 }}
              searchWords={[searchValue]}
              autoEscape
              textToHighlight={text ? text.toString() : ''}
            />
          ) : (
            <span className={styles.cellValue}>{renderRecordValue(text)}</span>
          )}
        </span>
        {record?.isEnterprise ? <span className={styles.enterpriseTag}>Enterprise</span> : null}
        {hasChildren ? (
          <span
            className={styles.nameExpandToggle}
            role='button'
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand?.(record.key);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onToggleExpand?.(record.key);
              }
            }}>
            {isExpanded ? <CaretUpFilled /> : <CaretDownFilled />}
          </span>
        ) : null}
      </div>
    );
    },
  },
  {
    title: 'Users',
    dataIndex: 'usersNumber',
    key: 'usersNumber',
    align: 'center',
    render: (text) => {
      return <div className={styles.centerCellValue}>{renderRecordValue(text)}</div>;
    },
    width: 150,
  },
  {
    title: 'State(s)',
    dataIndex: 'state',
    key: 'state',
    render: (text) => <div className={styles.cellValue}>{renderRecordValue(text)}</div>,
    width: 270,
  },
  {
    title: 'Status',
    dataIndex: 'disabled',
    key: 'disabled',
    render: (disabled, record) => (
      <div className={styles.cellValue}>{renderOrgStatus(disabled, record.adminStatus)}</div>
    ),
    width: 130,
  },
  {
    title: '',
    key: 'buttons',
    dataIndex: 'buttons',
    render: (buttons) => <div className={styles.btnPanel}>{buttons.map((Component) => Component)}</div>,
    width: 560,
  },
];

export const transformData = ({
  data,
  handleManageOrganization,
  switchOrganizationStatus,
  handleSendPassword,
  handleResendInvitation,
  handleViewDashboard,
  isEnterpriseAdmin = false,
  parentOrgId = null,
  handleAddClinic, // Add this new handler
  isViewDashboardMode,
  parentHasActiveAdmin = true,
  onBlockActivation,
}) => {
  return data.map((value) => {

    // Define buttons based on organization status and type
    let buttons = [];

    if (value.disabled) {
      const isChildFacility = Boolean(value.parentOrgId) && !value.isEnterprise;
      const blockedByEnterpriseAdmin = isChildFacility && !parentHasActiveAdmin;

      buttons = [
        <div key={0} className={styles.restoreAccess}>
          <Button
            myClassName={styles.navigationBtn}
            onClick={blockedByEnterpriseAdmin
              ? () => onBlockActivation?.(value)
              : switchOrganizationStatus({ disabled: false, id: value.id })}>
            Activate Organization
          </Button>
        </div>,
      ];
    } else {
      const hasActiveAdmin = value.adminStatus === USER_STATUSES.ACTIVE;
      const hasResettableAdmin = Boolean(value.admin) && value.adminStatus !== USER_STATUSES.DISABLED;

      // If isEnterpriseAdmin is true, add the "Add Facility" button
      if (value.isEnterprise) {
        buttons = [
          ...(hasActiveAdmin ? [
            <div key="view-dashboard" className={styles.tableButton}>
              <Button
                myClassName={styles.navigationBtn}
                onClick={handleViewDashboard({
                  organizationId: value.id,
                  role: value.isEnterprise ? USER_ROLES.ENTERPRISE_ADMIN : USER_ROLES.HO_ADMIN,
                  organizationName: value.name,
                  isViewDashboardMode
                })}>
                View Dashboard
              </Button>
            </div>,
          ] : []),
          <div key="add-facility" className={styles.tableButton}>
            <Button
              myClassName={styles.navigationBtn}
              onClick={handleAddClinic(value.id)}>
              Add Facility
            </Button>
          </div>
        ];
      } else {
        // Only add View Dashboard and Reset Credentials buttons if not an enterprise admin
        buttons = [
          ...(hasActiveAdmin ? [
            <div key="view-dashboard" className={styles.tableButton}>
              <Button
                myClassName={styles.navigationBtn}
                onClick={handleViewDashboard({
                  organizationId: value.id,
                  role: USER_ROLES.HO_ADMIN,
                  organizationName: value.name,
                  parentOrgId: value.parentOrgId,
                  needsBillingSetup: Boolean(value.parentOrgId && !value.hoPricingId),
                })}>
                View Dashboard
              </Button>
            </div>,
          ] : []),
          ...(hasResettableAdmin ? [renderResetCredentialsButton({
            invited: value.adminStatus === USER_STATUSES.INVITED || value.adminStatus === USER_STATUSES.EXPIRED,
            handleSendPassword: handleSendPassword(value?.admin),
            handleResendInvitation: handleResendInvitation(value?.admin),
            resetButtonText: '(Send) Reset Password to Admin',
            resendButtonText: 'Resend Invitation to Admin',
          })] : []),
        ];
      }
      
      // Always add Manage Organization button
      buttons.push(
        <div key={buttons.length} className={styles.tableButton}>
          <Button myClassName={styles.navigationBtn} onClick={handleManageOrganization(value.id)}>
            Manage Organization
          </Button>
        </div>
      );
    }

    const baseRecord = {
      key: value.id,
      name: value.name,
      usersNumber: value.usersNumber,
      state: value?.state,
      disabled: value.disabled,
      adminStatus: value.adminStatus,
      isEnterprise: value.isEnterprise,
      parentOrgId: value?.parentOrgId,
      hoPricingId: value?.hoPricingId,
      buttons: buttons,
    };

    // If this record has children, process them recursively with the same transform logic
    if (value.children && value.children.length > 0) {
      baseRecord.children = transformData({
        data: value.children,
        handleManageOrganization,
        switchOrganizationStatus,
        handleSendPassword,
        handleResendInvitation,
        handleViewDashboard,
        isEnterpriseAdmin: false, // Children are not enterprise admins
        parentOrgId: value.id,
        handleAddClinic,
        isViewDashboardMode,
        parentHasActiveAdmin: !value.disabled && value.adminStatus === USER_STATUSES.ACTIVE,
        onBlockActivation,
      });
    }

    return baseRecord;
  });
};


